import styled, { css } from 'styled-components';
import { motion } from 'motion/react';
import { NavLink } from 'react-router';
import { media } from '@styles/breakpoints';

export const HeaderWrapper = styled(motion.header)<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  height: ${({ theme }) => theme.layout.headerHeightMobile};
  display: flex;
  align-items: center;
  transition:
    background-color 0.4s ease,
    box-shadow 0.4s ease,
    height 0.4s ease;

  ${media.tablet} {
    height: ${({ theme }) => theme.layout.headerHeight};
  }

  /* Estado transparente — topo da página. */
  background-color: transparent;

  /* Estado sólido — após scroll. */
  ${({ $scrolled, theme }) =>
    $scrolled &&
    css`
      background-color: rgba(12, 22, 35, 0.88);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      box-shadow: ${theme.shadows.lg};
    `}
`;

export const HeaderInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin-inline: auto;
  padding-inline: ${({ theme }) => theme.spacing[5]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[6]};

  ${media.tablet} {
    padding-inline: ${({ theme }) => theme.spacing[8]};
  }

  ${media.desktop} {
    padding-inline: ${({ theme }) => theme.spacing[12]};
  }
`;

/* ── Navegação desktop ─────────────────────────────────── */

export const Nav = styled.nav`
  display: none;

  ${media.tablet} {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

export const NavItem = styled(NavLink)`
  position: relative;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  color: rgba(255, 255, 255, 0.75);
  border-radius: ${({ theme }) => theme.radii.sm};
  transition: color 0.2s ease;
  text-transform: uppercase;
  font-size: 0.7rem;

  /* Linha de active abaixo do link. */
  &::after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: ${({ theme }) => theme.spacing[4]};
    right: ${({ theme }) => theme.spacing[4]};
    height: 2px;
    background-color: ${({ theme }) => theme.semantic.brand.accent};
    border-radius: ${({ theme }) => theme.radii.full};
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @media (hover: hover) {
    &:hover {
      color: rgba(255, 255, 255, 1);

      &::after {
        transform: scaleX(0.5);
      }
    }
  }

  &.active {
    color: ${({ theme }) => theme.semantic.brand.accent};

    &::after {
      transform: scaleX(1);
    }
  }
`;

export const NavCta = styled.div`
  display: none;

  ${media.tablet} {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

/* ── Menu mobile ─────────────────────────────────────────── */

export const MenuButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  ${media.tablet} {
    display: none;
  }
`;

export const MenuLine = styled(motion.span)`
  display: block;
  width: 24px;
  height: 2px;
  background-color: white;
  border-radius: 2px;
  transform-origin: center;
`;

/* ── Mobile drawer ───────────────────────────────────────── */

export const MobileDrawer = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.navy[950]} 0%,
    ${({ theme }) => theme.colors.navy[900]} 100%
  );
  padding: ${({ theme }) => theme.spacing[6]};
  overflow-y: auto;

  ${media.tablet} {
    display: none;
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[10]};
`;

export const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const DrawerNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[4]} 0`};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: rgba(255, 255, 255, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  transition: color 0.2s ease;

  &.active,
  &:hover {
    color: ${({ theme }) => theme.semantic.brand.accent};
  }
`;

export const DrawerFooter = styled.div`
  margin-top: ${({ theme }) => theme.spacing[10]};
  padding-top: ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

export const DrawerContact = styled.a`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.45);
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;
